import { FC, useEffect, useState } from "react";
import { Box, Divider, Paper, Tab, Tabs } from "@mui/material";

import { PERMISSIONS } from "../../types/auth";
import { useHasPermission } from "../../contexts/permissions/useHasPermission";
import { useObservabilityStore } from "../../hooks/useObservabilityStore";

import FinancialSummaryBar from "../../components/dashboard/components/observability/FinancialSummaryBar";
import ImpactMetricsCard from "../../components/dashboard/components/observability/ImpactMetricsCard";
import CostMetricsCard from "../../components/dashboard/components/observability/CostMetricsCard";
import ByTypePie from "../../components/dashboard/components/observability/ByTypePie";
import CasesByUserBar from "../../components/dashboard/components/observability/CasesByUserBar";
import CasesByPayerPie from "../../components/dashboard/components/observability/CasesByPayerPie";
import UsageActivityFeedCard, {
  UsageActivityFeedCardActions,
} from "../../components/dashboard/components/observability/UsageActivityFeedCard";
import SuccessFeeByClaimCard, {
  SuccessFeeByClaimCardActions,
} from "../../components/dashboard/components/observability/SuccessFeeByClaimCard";

import Row from "../../components/ui/core/Row";

// REV-1762 Financial Impact dashboard. One card surface split by separator
// lines (same chrome as the old Usage layout): the top half is three columns —
// the metric stack (Summary / Impact / Cost), the Denials charts, and the
// Cases-by-Type chart — and the activity feed fills the bottom half. Every
// section follows the global date filter.
const ObservabilityDashboard: FC = () => {
  const hasBillingPermission = useHasPermission(PERMISSIONS.billing);
  const [bottomTab, setBottomTab] = useState<"activity" | "successFee">("activity");
  const { loadPageOptions } = useObservabilityStore();

  // Filter options span all data (not the date range), so load them once.
  // Both options endpoints are billing-member only.
  useEffect(() => {
    if (hasBillingPermission) loadPageOptions();
  }, [hasBillingPermission, loadPageOptions]);

  return (
    <Row
      sx={{
        height: "100%",
        overflow: "hidden",
        width: "100%",
        backgroundColor: "secondary.main",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          padding: 4,
          paddingTop: 0,
          overflow: "hidden",
        }}
      >
        <Paper
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            // Flatten inner cards so the seams read as dividers, not stacked
            // cards.
            "& .MuiPaper-root": {
              boxShadow: "none",
              borderRadius: 0,
              backgroundColor: "transparent",
            },
          }}
        >
          {/* Top section: metric stack | By-type (cases/cost toggle) |
              Cases-by-user | Cases-by-payer pies. Fixed to a compact height
              that hugs the metric stack, so there's no empty space and the
              activity feed gets the rest. */}
          <Box
            sx={{
              flex: "0 0 300px",
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "row",
              overflow: "hidden",
            }}
          >
            {/* Summary / Impact / Cost stacked in the first column. */}
            <Box
              sx={{
                flex: "0 0 480px",
                minWidth: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                overflowY: "auto",
              }}
            >
              <FinancialSummaryBar />
              <ImpactMetricsCard />
              <CostMetricsCard />
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
              <ByTypePie />
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
              <CasesByUserBar />
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
              <CasesByPayerPie />
            </Box>
          </Box>

          <Divider />

          {/* Bottom section: activity feed and (billing members only —
              REV-1824: recovery is charged once per claim, so success fees
              live in the per-claim table, not the per-case feed) the
              per-claim success fee table, switched via tabs. */}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", width: "100%" }}>
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {hasBillingPermission ? (
                <Tabs value={bottomTab} onChange={(_, v) => setBottomTab(v)}>
                  <Tab value="activity" label="Transactions" />
                  <Tab value="successFee" label="Contingency" />
                </Tabs>
              ) : (
                <Box />
              )}

              {hasBillingPermission && bottomTab === "successFee" ? (
                <SuccessFeeByClaimCardActions />
              ) : (
                <UsageActivityFeedCardActions />
              )}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
              {hasBillingPermission && bottomTab === "successFee" ? (
                <SuccessFeeByClaimCard />
              ) : (
                <UsageActivityFeedCard />
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Row>
  );
};

export default ObservabilityDashboard;
